import React, { useEffect, useRef } from 'react';
import { EscolhaRegistrada, MOTIVO_SEM_COMPRA_LABEL } from '../../../interfaces/PortalCotacoes';
// Mesma folha dos modais de detalhe da Auditoria (Planejamento, Liberação Administrativa).
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaPropostaModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Escolhas do comprador num salvamento (14/08/2026, pedido do Victor: a timeline dizia
 * "Escolhas do comprador salvas — 1 item" e ninguém sabia QUAIS escolhas).
 *
 * ⚠️ Este modal NÃO consulta nada: as escolhas vêm dentro do próprio evento, porque foram
 * gravadas no log no momento da ação. É a única forma honesta — a decisão de um item é
 * sobrescrita a cada novo salvamento, então consultar a tabela agora mostraria o estado de
 * hoje no lugar do que este evento registrou.
 *
 * `escolhas === null` é registro ANTERIOR a 14/08/2026, quando só a contagem era gravada.
 * Nesse caso o modal diz que o detalhe não foi registrado — não há como recuperá-lo.
 */

interface AuditoriaDecisoesModalProps {
  escolhas: EscolhaRegistrada[] | null;
  em: string;
  autor: string | null;
  onFechar: () => void;
  /**
   * O mesmo modal serve DOIS eventos (14/08/2026): "Escolhas do comprador salvas" e "Cesta
   * fechada" — os dois registram o mesmo formato (as escolhas num instante), e o que muda é
   * o título, o rótulo de autoria e o total. Duplicar o componente faria os dois divergirem
   * na primeira manutenção.
   */
  titulo?: string;
  rotuloAutor?: string;
  total?: number | null;
  /** Desfecho do fechamento (`AGUARDANDO_APROVACAO` ou `GERANDO_PEDIDOS`). */
  resultado?: string | null;
}

function moeda(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

const AuditoriaDecisoesModal: React.FC<AuditoriaDecisoesModalProps> = ({
  escolhas,
  em,
  autor,
  onFechar,
  titulo = 'Escolhas do Comprador',
  rotuloAutor = 'Salvas por',
  total = null,
  resultado = null,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onFechar]);

  const acimaDoMenorPreco = (escolhas ?? []).filter((e) => !e.menorPreco && e.fornecedor).length;

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>{titulo}</h2>
          <button className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">
            <strong>{rotuloAutor}:</strong> <span>{autor || 'Sistema'}</span>
          </div>
          <div className="metadata-item">
            <strong>Em:</strong> <span>{dataHora(em)}</span>
          </div>
          {total !== null && (
            <div className="metadata-item">
              <strong>Total:</strong> <span>{moeda(total)}</span>
            </div>
          )}
        </div>

        <div className="planning-modal-body">
          {escolhas === null ? (
            <p className="auditoria-proposta-aviso">
              Este registro é anterior a agosto de 2026, quando o sistema guardava apenas a{' '}
              <strong>quantidade</strong> de itens salvos. O detalhe do que foi escolhido não foi registrado à
              época e não pode ser recuperado.
            </p>
          ) : (
            <>
              {/* Desfecho do fechamento: para onde a cesta foi ao ser fechada. */}
              {resultado && (
                <p className="auditoria-escolhas-nota">
                  {resultado === 'AGUARDANDO_APROVACAO'
                    ? 'Ao fechar, a cesta foi enviada para aprovação do gestor.'
                    : 'Ao fechar, a cesta foi liberada para geração dos pedidos.'}
                </p>
              )}

              {acimaDoMenorPreco > 0 && (
                <p className="auditoria-proposta-aviso">
                  {acimaDoMenorPreco === 1
                    ? '1 escolha acima do menor preço'
                    : `${acimaDoMenorPreco} escolhas acima do menor preço`}{' '}
                  — passam pela aprovação do gestor ao fechar a cesta.
                </p>
              )}

              <div className="auditoria-proposta-tabela-wrapper">
                <table className="auditoria-proposta-tabela">
                  <thead>
                    <tr>
                      <th>Peça</th>
                      <th>Fornecedor escolhido</th>
                      <th>Valor</th>
                      <th>Menor preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {escolhas.map((escolha, indice) => (
                      <tr key={`${escolha.item}-${indice}`}>
                        <td>
                          <div>{escolha.item}</div>
                          {escolha.justificativa && (
                            <div className="auditoria-proposta-secundario">
                              Justificativa: {escolha.justificativa}
                            </div>
                          )}
                        </td>
                        {/*
                          Item sem compra ocupa a linha inteira: fornecedor vazio com valor
                          "—" se confundiria com dado faltando, e a diferença importa — foi
                          uma decisão, não uma omissão.
                        */}
                        {escolha.fornecedor ? (
                          <>
                            <td>{escolha.fornecedor}</td>
                            <td>{moeda(escolha.valor)}</td>
                            <td>{escolha.menorPreco ? 'Sim' : 'Não'}</td>
                          </>
                        ) : (
                          <td colSpan={3} className="auditoria-proposta-nao-cotou">
                            Sem compra
                            {escolha.semCompra
                              ? ` — ${MOTIVO_SEM_COMPRA_LABEL[escolha.semCompra] ?? escolha.semCompra}`
                              : ''}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="auditoria-proposta-nota">
                Estas são as escolhas como ficaram registradas neste salvamento. Salvamentos posteriores podem
                tê-las alterado.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaDecisoesModal;
